import { useRef, useState, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Eraser, FloppyDisk } from "@phosphor-icons/react";

export default function SignatureModal({ open, onOpenChange, onConfirm }) {
    const ref = useRef(null);
    const wrapRef = useRef(null);
    const [empty, setEmpty] = useState(true);

    // Match the canvas internal buffer (in device pixels) to the displayed CSS
    // size. Without this, touch coordinates on iPad / retina screens land in
    // the wrong region of the internal buffer, `getTrimmedCanvas()` throws
    // IndexSizeError, and `isEmpty()` reports true — so the Save button
    // appears to do nothing.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const resize = () => {
            if (cancelled) return;
            const canvas = ref.current?.getCanvas?.();
            const wrap = wrapRef.current;
            if (!canvas || !wrap) return;
            const dpr = window.devicePixelRatio || 1;
            const w = wrap.clientWidth || 480;
            const h = 200;
            // Only resize when it actually changed to avoid clearing the drawing
            if (canvas.width === w * dpr && canvas.height === h * dpr) return;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            const ctx = canvas.getContext("2d");
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.scale(dpr, dpr);
            ref.current?.clear();
            setEmpty(true);
        };
        // Delay to next tick so the dialog is mounted and has a bounding box
        const t = setTimeout(resize, 60);
        window.addEventListener("resize", resize);
        window.addEventListener("orientationchange", resize);
        return () => {
            cancelled = true;
            clearTimeout(t);
            window.removeEventListener("resize", resize);
            window.removeEventListener("orientationchange", resize);
        };
    }, [open]);

    const clear = () => { ref.current?.clear(); setEmpty(true); };

    const save = () => {
        if (empty || !ref.current) {
            setEmpty(true);
            return;
        }
        // Prefer the trimmed canvas (smaller PNG), fall back to raw canvas on
        // browsers where trimming throws (small strokes, retina rounding, etc.)
        let dataUrl = "";
        try {
            dataUrl = ref.current.getTrimmedCanvas().toDataURL("image/png");
        } catch {
            try {
                dataUrl = ref.current.getCanvas().toDataURL("image/png");
            } catch {
                dataUrl = ref.current.toDataURL?.("image/png") || "";
            }
        }
        if (!dataUrl) return;
        onConfirm(dataUrl);
        onOpenChange(false);
        setTimeout(() => ref.current?.clear(), 200);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg" data-testid="signature-modal">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Sign the document</DialogTitle>
                    <DialogDescription className="text-sm text-slate-500">Draw your signature with mouse or touch. It will be embedded in the PDF.</DialogDescription>
                </DialogHeader>
                <div
                    ref={wrapRef}
                    className="border-2 border-dashed border-slate-300 rounded-md bg-white overflow-hidden"
                    style={{ touchAction: "none" }}
                >
                    <SignatureCanvas
                        ref={ref}
                        penColor="#0F1E3D"
                        canvasProps={{
                            className: "block rounded-md",
                            style: { touchAction: "none", width: "100%", height: "200px" },
                            "data-testid": "signature-canvas",
                        }}
                        onBegin={() => setEmpty(false)}
                        onEnd={() => {
                            if (ref.current && !ref.current.isEmpty()) setEmpty(false);
                        }}
                    />
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={clear} data-testid="signature-clear">
                        <Eraser size={14} className="mr-1.5" /> Clear
                    </Button>
                    <Button
                        onClick={save}
                        disabled={empty}
                        className="bg-[#0F1E3D] hover:bg-[#1A2C57] disabled:opacity-50"
                        data-testid="signature-confirm"
                    >
                        <FloppyDisk size={14} className="mr-1.5" /> Save & Export PDF
                    </Button>
                </DialogFooter>
                {empty && (
                    <p className="text-[11px] text-slate-400 text-center mt-1">
                        Draw your signature above before saving.
                    </p>
                )}
            </DialogContent>
        </Dialog>
    );
}
